/**
 * Onboarding Page
 *
 * Full-height wizard frame (fixed header + progress strip + scrolling body +
 * fixed footer) adopted from the design-system prototype
 * (docs/design-system/templates/app/onboarding.jsx). Renders bare/full-screen
 * (no app shell), like /login and /pending-approval.
 */

import { type ComponentType, type RefObject, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Briefcase,
  Building2,
  Check,
  ChevronRight,
  ClipboardCheck,
  ShieldCheck,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Logo } from "@/app/shell/Logo";
import { useAuth } from "@/contexts/useAuth";
import { useOnboarding } from "@/features/onboarding/hooks/useOnboarding";
import { useUnsavedChanges } from "@/services/app-update/unsaved-changes";
import { Step1Business } from "@/features/onboarding/components/Step1Business";
import { Step2Details } from "@/features/onboarding/components/Step2Details";
import { Step3Confirm } from "@/features/onboarding/components/Step3Confirm";
import { OnboardingRegionSection } from "@/features/onboarding/components/OnboardingRegionSection";
import { isValidPolishNip, normalizeNip } from "@/utils/nip";
import {
  DECLARED_CERTIFICATE_OTHER_CODE,
  INDUSTRY_SECTOR_OTHER_CODE,
  isIndustrySectorCode,
} from "@/constants/company-form-options";
import {
  type AppLanguage,
  getStoredDefaultMarket,
  getStoredReportLanguage,
  setDefaultMarket,
  setReportLanguage,
} from "@/utils/language-preference";
import type { Market } from "@vardic/shared/types.ts";
import { updateUserPreferences } from "@/services/api-client";

interface FormData {
  business_activity: string;
  company_name: string;
  legal_form: string;
  legal_form_other: string;
  industry_sector: string[];
  industry_sector_other: string;
  nip: string;
  declared_certificate: string[];
  declared_certificate_other: string;
}
interface FormErrors {
  business_activity?: string;
  company_name?: string;
  legal_form?: string;
  legal_form_other?: string;
  industry_sector?: string;
  industry_sector_other?: string;
  nip?: string;
  declared_certificate?: string;
  declared_certificate_other?: string;
}

const TOTAL_STEPS = 3;

// Per-step icon for the StepHead eyebrow. Copy (eyebrow/title/subtitle) lives in
// the `onboarding:stepHead` namespace so it stays localized + casing-guarded.
const STEP_ICON: Record<number, ComponentType<{ className?: string }>> = {
  1: Briefcase,
  2: Building2,
  3: ClipboardCheck,
};

// Maps each validated field to the DOM id that should receive focus when
// that field fails validation on a blocked "Next" click (WCAG 2.4.3 focus
// order). Only fields that can independently fail carry an id here.
const FIELD_FOCUS_ID: Partial<Record<keyof FormErrors, string>> = {
  business_activity: "business-activity",
  company_name: "company-name",
  legal_form: "legal-form",
  legal_form_other: "legal-form-other",
  industry_sector: "industry-sector-group",
  industry_sector_other: "industry-sector-other",
  nip: "company-nip",
  declared_certificate_other: "declared-certificate-other",
};

// Priority order used to pick the *first* invalid field per step, matching
// the order fields are validated (and rendered) in.
const STEP1_FIELD_ORDER: Array<keyof FormErrors> = ["business_activity"];
const STEP2_FIELD_ORDER: Array<keyof FormErrors> = [
  "company_name",
  "legal_form",
  "legal_form_other",
  "industry_sector",
  "industry_sector_other",
  "nip",
  "declared_certificate_other",
];

function focusFirstInvalidField(
  fieldErrors: FormErrors,
  order: Array<keyof FormErrors>,
) {
  for (const key of order) {
    if (fieldErrors[key]) {
      const id = FIELD_FOCUS_ID[key];
      if (id) document.getElementById(id)?.focus();
      return;
    }
  }
}

function StepHead(
  { icon: Icon, eyebrow, title, subtitle, headingRef }: {
    icon: ComponentType<{ className?: string }>;
    eyebrow: string;
    title: string;
    subtitle: string;
    headingRef: RefObject<HTMLHeadingElement | null>;
  },
) {
  return (
    <div className="mb-7">
      <div className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--primary)]">
        <Icon className="h-3.5 w-3.5" />
        {eyebrow}
      </div>
      <h1
        ref={headingRef}
        tabIndex={-1}
        className="font-heading text-3xl font-semibold tracking-tight text-[var(--foreground)] focus:outline-none"
      >
        {title}
      </h1>
      <p className="mt-2 max-w-xl text-sm leading-6 text-[var(--muted-foreground)]">
        {subtitle}
      </p>
    </div>
  );
}

export default function Onboarding() {
  const { t } = useTranslation(["onboarding", "validation", "auth", "common"]);
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const { submitOnboarding, isLoading, error } = useOnboarding();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    business_activity: "",
    company_name: "",
    legal_form: "",
    legal_form_other: "",
    industry_sector: [],
    industry_sector_other: "",
    nip: "",
    declared_certificate: [],
    declared_certificate_other: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  // Results-language DEFAULT (set once here, inherited by every audit, editable
  // later in Settings). Persists via the preferences endpoint, not the
  // onboarding company payload — see handleSubmit.
  const [langPref, setLangPref] = useState<AppLanguage>(getStoredReportLanguage);
  const [marketPref, setMarketPref] = useState<Market>(getStoredDefaultMarket);
  // When true, render the completion screen instead of the wizard steps.
  const [justCompleted, setJustCompleted] = useState(false);
  useUnsavedChanges(
    !justCompleted &&
      Object.values(formData).some((value) =>
        Array.isArray(value) ? value.length > 0 : value !== ""
      ),
  );
  // Set synchronously the moment submit begins so the profile refresh inside
  // submitOnboarding (which flips onboardingCompleted → true) does NOT trigger
  // the guard redirect before the completion screen renders.
  const completingRef = useRef(false);
  // Heading receives focus on every step transition (WCAG 2.4.3 focus order).
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    if (profile?.onboardingCompleted === true && !completingRef.current) {
      void navigate("/", { replace: true });
    }
  }, [profile, navigate]);

  useEffect(() => {
    // Skip the initial mount: the heading should only steal focus on an
    // actual step transition, not on first paint.
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    stepHeadingRef.current?.focus();
  }, [currentStep]);

  const validateStep1 = (): FormErrors => {
    const newErrors: FormErrors = {};

    const activity = formData.business_activity.trim();
    if (!activity) {
      newErrors.business_activity = t("validation:required");
      setErrors(newErrors);
      return newErrors;
    }

    if (activity.length < 10) {
      newErrors.business_activity = t(
        "validation:company.businessActivityMinLength",
        { count: 10 },
      );
      setErrors(newErrors);
      return newErrors;
    }

    if (activity.length > 500) {
      newErrors.business_activity = t(
        "validation:company.businessActivityMaxLength",
        { count: 500 },
      );
      setErrors(newErrors);
      return newErrors;
    }

    setErrors({});
    return newErrors;
  };

  const validateStep2 = (): FormErrors => {
    const newErrors: FormErrors = {};

    if (!formData.company_name.trim()) {
      newErrors.company_name = t("validation:company.nameRequired");
    }

    if (!formData.legal_form) {
      newErrors.legal_form = t("validation:company.legalFormRequired");
    }
    if (
      formData.legal_form === "other" &&
      !formData.legal_form_other.trim()
    ) {
      newErrors.legal_form_other = t(
        "validation:company.legalFormOtherRequired",
      );
    }

    if (formData.industry_sector.length === 0) {
      newErrors.industry_sector = t(
        "validation:company.industrySectorRequired",
      );
    }
    if (
      formData.industry_sector.includes(INDUSTRY_SECTOR_OTHER_CODE) &&
      !formData.industry_sector_other.trim()
    ) {
      newErrors.industry_sector_other = t(
        "validation:company.industrySectorOtherRequired",
      );
    }

    if (!formData.nip.trim()) {
      newErrors.nip = t("validation:company.nipRequired");
    } else if (!isValidPolishNip(formData.nip)) {
      newErrors.nip = t("validation:company.nipInvalid");
    }

    if (
      formData.declared_certificate.includes(DECLARED_CERTIFICATE_OTHER_CODE) &&
      !formData.declared_certificate_other.trim()
    ) {
      newErrors.declared_certificate_other = t(
        "validation:company.declaredCertificateOtherRequired",
      );
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleNext = () => {
    if (currentStep === 1) {
      const stepErrors = validateStep1();
      if (Object.keys(stepErrors).length > 0) {
        focusFirstInvalidField(stepErrors, STEP1_FIELD_ORDER);
        return;
      }
    }

    if (currentStep === 2) {
      const stepErrors = validateStep2();
      if (Object.keys(stepErrors).length > 0) {
        focusFirstInvalidField(stepErrors, STEP2_FIELD_ORDER);
        return;
      }
    }

    setCurrentStep((prev) => Math.min(prev + 1, TOTAL_STEPS));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    setErrors({});
  };

  const handleSubmit = async () => {
    // Intent to complete: suppress the guard redirect (see completingRef).
    completingRef.current = true;
    try {
      await submitOnboarding({
        businessActivity: formData.business_activity,
        companyName: formData.company_name,
        legalForm: formData.legal_form,
        legalFormOther: formData.legal_form === "other"
          ? formData.legal_form_other.trim()
          : undefined,
        industrySector: formData.industry_sector,
        industrySectorOther: formData.industry_sector.includes(
            INDUSTRY_SECTOR_OTHER_CODE,
          )
          ? formData.industry_sector_other.trim()
          : undefined,
        nip: normalizeNip(formData.nip),
        // pirx-lus53: declared_certificate is an optional multi-select. An
        // empty selection => not declared (omit the field). When "other" is
        // selected, send the trimmed free-text alongside.
        declaredCertificate: formData.declared_certificate.length > 0
          ? formData.declared_certificate
          : undefined,
        declaredCertificateOther:
          formData.declared_certificate.includes(DECLARED_CERTIFICATE_OTHER_CODE)
            ? formData.declared_certificate_other.trim() || undefined
            : undefined,
      });
      // Persist the audit defaults (target market + results language) chosen
      // during onboarding. Best-effort: a failure here must not block
      // onboarding completion (the company payload already succeeded), and the
      // values are cached locally regardless.
      setReportLanguage(langPref);
      setDefaultMarket(marketPref);
      void updateUserPreferences({
        report_language: langPref,
        default_market: marketPref,
      }).catch(() => {});
      setJustCompleted(true);
    } catch (err) {
      // Submit failed: allow the guard redirect again and surface the banner.
      completingRef.current = false;
      // Hook already populated its `error` state for the banner. Log the raw
      // error too — useful for dev console diagnostics; the banner stays
      // localized via i18n.
      console.error("Onboarding submission failed:", err);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    void navigate("/login");
  };

  const updateBusinessActivity = (value: string) => {
    setFormData((prev) => ({ ...prev, business_activity: value }));
    if (errors.business_activity) {
      setErrors((prev) => ({ ...prev, business_activity: undefined }));
    }
  };

  const updateDetails = (
    field: keyof FormData,
    value: string | string[],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const StepIcon = STEP_ICON[currentStep] ?? Briefcase;

  const firstSector = formData.industry_sector[0];
  const sectorSummary = firstSector
    ? (firstSector === INDUSTRY_SECTOR_OTHER_CODE &&
        formData.industry_sector_other.trim()
      ? formData.industry_sector_other.trim()
      : isIndustrySectorCode(firstSector)
      ? t(`onboarding:step2.industrySector.options.${firstSector}`)
      : firstSector)
    : t("common:labels.none");
  const doneSummary = [
    {
      label: t("onboarding:done.summary.company"),
      value: formData.company_name.trim() || t("common:labels.none"),
    },
    {
      label: t("onboarding:done.summary.sector"),
      value: sectorSummary,
    },
    {
      label: t("onboarding:done.summary.language"),
      value: langPref === "en"
        ? t("onboarding:region.language.en")
        : t("onboarding:region.language.pl"),
    },
  ];

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-[var(--background)]">
      {/* Header */}
      <header className="flex h-[62px] shrink-0 items-center gap-3 border-b border-[var(--border)] bg-[var(--card)] px-6">
        <Logo size={30} />
        <div className="flex flex-col leading-tight">
          {/* eslint-disable-next-line i18next/no-literal-string -- brand wordmark, not translatable */}
          <span className="text-sm font-semibold tracking-tight text-[var(--foreground)]">
            Vardic
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--muted-foreground)]">
            {t("onboarding:header.eyebrow")}
          </span>
        </div>
        <div className="flex-1" />
        {!justCompleted && (
          <span className="font-mono text-xs text-[var(--muted-foreground)]">
            {t("onboarding:progress.step", {
              current: currentStep,
              total: TOTAL_STEPS,
            })}
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSignOut}
          data-test="onboarding-signout"
        >
          {t("common:nav.logout")}
        </Button>
      </header>

      {/* Progress strip */}
      {!justCompleted && (
        <div
          className="flex shrink-0 gap-1.5 border-b border-[var(--border)] bg-[var(--card)] px-6 py-2"
          data-test="onboarding-progress-segments"
        >
          {[1, 2, 3].map((step) => (
            <div
              key={step}
              className={`h-1 flex-1 rounded-full transition-colors duration-[var(--dur-slow)] ${
                step <= currentStep
                  ? "bg-primary"
                  : "bg-[color:color-mix(in_srgb,var(--accent)_78%,var(--card))]"
              }`}
            />
          ))}
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[640px] px-6 py-12">
          {justCompleted
            ? (
              <div
                className="flex flex-col items-center py-6 text-center"
                data-test="onboarding-complete"
              >
                <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[color:color-mix(in_srgb,var(--primary)_14%,var(--card))] text-[var(--primary)]">
                  <Check className="h-8 w-8" />
                </div>
                <h1 className="font-heading text-3xl font-semibold tracking-tight text-[var(--foreground)]">
                  {t("onboarding:done.title")}
                </h1>
                <p className="mt-3 max-w-md text-sm leading-6 text-[var(--muted-foreground)]">
                  {t("onboarding:done.subtitle")}
                </p>
                <div className="mt-7 w-full max-w-sm rounded-[var(--r-lg)] border border-[var(--border)] bg-[var(--card)] px-5 text-left">
                  {doneSummary.map((row, index) => (
                    <div
                      key={row.label}
                      className={`flex items-center justify-between gap-4 py-3.5 ${
                        index < doneSummary.length - 1
                          ? "border-b border-[var(--border)]"
                          : ""
                      }`}
                    >
                      <span className="text-sm text-[var(--muted-foreground)]">
                        {row.label}
                      </span>
                      <span className="text-right text-sm font-medium text-[var(--foreground)]">
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
                <Button
                  variant="cta"
                  size="lg"
                  className="mt-8"
                  onClick={() => void navigate("/")}
                  data-test="onboarding-complete-cta"
                >
                  <ShieldCheck />
                  {t("onboarding:done.cta")}
                </Button>
              </div>
            )
            : (
              <>
                <StepHead
                  icon={StepIcon}
                  eyebrow={t(`onboarding:stepHead.${currentStep}.eyebrow`)}
                  title={t(`onboarding:stepHead.${currentStep}.title`)}
                  subtitle={t(`onboarding:stepHead.${currentStep}.subtitle`)}
                  headingRef={stepHeadingRef}
                />

                {error && (
                  <Alert
                    variant="error"
                    className="mb-6"
                    data-test="onboarding-submit-error"
                  >
                    <AlertDescription>
                      {t("onboarding:errors.submitFailed")}
                    </AlertDescription>
                  </Alert>
                )}

                {currentStep === 1 && (
                  <div data-test="onboarding-step1">
                    <Step1Business
                      value={formData.business_activity}
                      onChange={updateBusinessActivity}
                      error={errors.business_activity}
                    />
                  </div>
                )}

                {currentStep === 2 && (
                  <div data-test="onboarding-step2" className="space-y-5">
                    <Step2Details
                      values={{
                        company_name: formData.company_name,
                        legal_form: formData.legal_form,
                        legal_form_other: formData.legal_form_other,
                        industry_sector: formData.industry_sector,
                        industry_sector_other: formData.industry_sector_other,
                        nip: formData.nip,
                        declared_certificate: formData.declared_certificate,
                        declared_certificate_other:
                          formData.declared_certificate_other,
                      }}
                      onChange={updateDetails}
                      errors={errors}
                    />
                    <OnboardingRegionSection
                      defaultMarket={marketPref}
                      reportLanguage={langPref}
                      onChangeDefaultMarket={setMarketPref}
                      onChangeReportLanguage={setLangPref}
                    />
                  </div>
                )}

                {currentStep === 3 && (
                  <div data-test="onboarding-step3">
                    <Step3Confirm data={formData} />
                  </div>
                )}
              </>
            )}
        </div>
      </div>

      {/* Footer */}
      {!justCompleted && (
        <footer className="flex h-[72px] shrink-0 items-center justify-between border-t border-[var(--border)] bg-[var(--card)] px-6">
          {currentStep > 1
            ? (
              <Button
                variant="ghost"
                size="lg"
                onClick={handleBack}
                disabled={isLoading}
                data-test="onboarding-back-btn"
              >
                {t("onboarding:buttons.back")}
              </Button>
            )
            : <div />}

          {currentStep < TOTAL_STEPS
            ? (
              <Button
                variant="cta"
                size="lg"
                onClick={handleNext}
                data-test="onboarding-next-btn"
              >
                {t("onboarding:buttons.next")}
                <ChevronRight />
              </Button>
            )
            : (
              <Button
                variant="cta"
                size="lg"
                onClick={handleSubmit}
                disabled={isLoading}
                data-test="onboarding-submit-btn"
              >
                {isLoading
                  ? t("onboarding:buttons.saving")
                  : t("onboarding:buttons.confirm")}
              </Button>
            )}
        </footer>
      )}
    </div>
  );
}
